import React, { useState } from "react";
import Header from "../components/Header";
import { distributorService } from "../services/apiService";
import Toast from "../components/Toast";

const DistributorCreate = () => {
  const [form, setForm] = useState({
    name: "",
    mobileNo: "",
    email: "",
    address: "",
    status: "ACTIVE"
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [toast, setToast] = useState(null);

  const showMessage = (type, text, ms = 5000) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), ms);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.mobileNo.trim()) {
      newErrors.mobileNo = "Mobile number is required";
    } else if (!/^\d{10}$/.test(form.mobileNo.trim())) {
      newErrors.mobileNo = "Mobile number must be 10 digits";
    }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = "Invalid email format";
    }
    if (!form.address.trim()) newErrors.address = "Address is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      showMessage("error", "Please fix the errors before submitting");
      return;
    }

    setLoading(true);
    try {
      await distributorService.create({
        distributorName: form.name,
        contactNumber: form.mobileNo,
        email: form.email || null,
        address: form.address,
        status: form.status,
      });
      setToast({ identifier: `Distributor: ${form.name}`, message: "Distributor created successfully!" });
      setForm({ name: "", mobileNo: "", email: "", address: "", status: "ACTIVE" });
    } catch (err) {
      setToast({ type: "error", identifier: `Distributor: ${form.name}`, message: err.response?.data?.message || "Failed to create distributor" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setForm({
      name: "",
      mobileNo: "",
      email: "",
      address: "",
      status: "ACTIVE"
    });
    setErrors({});
    setMessage({ type: "", text: "" });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-6">
      <Header showSearch={false} showCart={false} />

      {toast && <Toast type={toast.type} identifier={toast.identifier} message={toast.message} onClose={() => setToast(null)} />}

      {/* Create Form Section */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-6">
        <h2 className="text-left text-base font-bold text-blue-800 px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          ➕ Create Distributor
        </h2>
        <div className="p-4">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Distributor Name <span className="text-red-500">*</span></label>
                <input type="text" name="name" value={form.name} onChange={handleChange}
                  placeholder="e.g. ABC Pharma"
                  className={`w-52 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${
                    errors.name ? "border-red-400 bg-red-50" : "border-blue-300"}`} />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Mobile Number <span className="text-red-500">*</span></label>
                <input type="tel" name="mobileNo" value={form.mobileNo}
                  onChange={(e) => { const value = e.target.value.replace(/\D/g, '').slice(0, 10); handleChange({ target: { name: 'mobileNo', value } }); }}
                  placeholder="e.g. 9876543210" maxLength="10"
                  className={`w-52 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${
                    errors.mobileNo ? "border-red-400 bg-red-50" : "border-blue-300"}`} />
                {errors.mobileNo && <p className="text-red-500 text-xs mt-1">{errors.mobileNo}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input type="email" name="email" value={form.email} onChange={handleChange}
                  placeholder="e.g. abc@pharma.com"
                  className={`w-52 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${
                    errors.email ? "border-red-400 bg-red-50" : "border-blue-300"}`} />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status <span className="text-red-500">*</span></label>
                <select name="status" value={form.status} onChange={handleChange}
                  className="w-52 px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none">
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Address <span className="text-red-500">*</span></label>
                <textarea name="address" value={form.address} onChange={handleChange}
                  placeholder="Enter complete address" rows="2"
                  className={`w-52 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${
                    errors.address ? "border-red-400 bg-red-50" : "border-blue-300"}`} />
                {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={handleReset}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-xs transition">
                <i className="fas fa-redo" /> Reset
              </button>
              <button type="submit" disabled={loading}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${
                  loading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"}`}>
                {loading ? <><i className="fas fa-spinner fa-spin" />Saving...</> : <><i className="fas fa-save" />Save Distributor</>}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default DistributorCreate;
